import React, { useState, useEffect } from 'react';
import {
  FiX, FiShield, FiAlertCircle, FiAlertTriangle, FiInfo,
  FiCheckCircle, FiRefreshCw, FiLock, FiFileText, FiGlobe,
} from 'react-icons/fi';
import {
  scanCompliance, getAiCache, saveAiCache,
  type ComplianceReport, type ComplianceFinding, type ComplianceFramework,
  type AiCacheHistoryEntry,
} from '../../api/ai';
import AiCacheActions from './AiCacheActions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
  architectureId?: string;
}

type FrameworkKey = 'cis' | 'iso27001' | 'rgpd' | 'hipaa' | 'pci_dss';

const FRAMEWORKS: { key: FrameworkKey; label: string; shortLabel: string; icon: React.ReactNode }[] = [
  { key: 'cis',      label: 'CIS Benchmarks',  shortLabel: 'CIS',      icon: <FiShield size={14} /> },
  { key: 'iso27001', label: 'ISO 27001:2022',   shortLabel: 'ISO 27001', icon: <FiFileText size={14} /> },
  { key: 'rgpd',     label: 'RGPD / GDPR',     shortLabel: 'RGPD',     icon: <FiGlobe size={14} /> },
  { key: 'hipaa',    label: 'HIPAA',            shortLabel: 'HIPAA',    icon: <FiLock size={14} /> },
  { key: 'pci_dss',  label: 'PCI-DSS v4',      shortLabel: 'PCI-DSS',  icon: <FiLock size={14} /> },
];

const SEVERITY_CONFIG = {
  critical: { label: 'Critique',  bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-300',    icon: <FiAlertCircle size={14} className="text-red-600" /> },
  high:     { label: 'Élevé',     bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', icon: <FiAlertTriangle size={14} className="text-orange-600" /> },
  medium:   { label: 'Moyen',     bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', icon: <FiInfo size={14} className="text-yellow-600" /> },
  low:      { label: 'Faible',    bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-300',   icon: <FiInfo size={14} className="text-blue-500" /> },
} as const;

const STATUS_CONFIG = {
  compliant:       { label: 'Conforme',         badge: 'bg-green-100 text-green-700 border-green-300' },
  partial:         { label: 'Partiel',           badge: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  non_compliant:   { label: 'Non conforme',      badge: 'bg-red-100 text-red-700 border-red-300' },
  not_applicable:  { label: 'Non applicable',    badge: 'bg-gray-100 text-gray-500 border-gray-300' },
};

function ScoreRing({ score }: { score: number }) {
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={88} height={88}>
        <circle cx={44} cy={44} r={r} fill="none" stroke="#e5e7eb" strokeWidth={8} />
        <circle cx={44} cy={44} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 44 44)" />
      </svg>
      <span className="absolute text-xl font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

function FrameworkBar({ score, label }: { score: number; label: string }) {
  const color = score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-400' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 text-gray-600 truncate">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className={`h-2 rounded-full ${color} transition-all duration-500`} style={{ width: `${score}%` }} />
      </div>
      <span className="w-8 text-right font-medium text-gray-700">{score}</span>
    </div>
  );
}

function FindingCard({ f }: { f: ComplianceFinding }) {
  const cfg = SEVERITY_CONFIG[f.severity] ?? SEVERITY_CONFIG.low;
  return (
    <div className={`border rounded-lg p-3 ${cfg.border} ${cfg.bg}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0">{cfg.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}>{cfg.label}</span>
            {f.control && (
              <span className="text-xs font-mono bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{f.control}</span>
            )}
            <span className="text-sm font-medium text-gray-800">{f.title}</span>
          </div>
          {f.resource && f.resource !== 'global' && (
            <p className="text-xs text-gray-500 mb-1">Ressource : <span className="font-medium">{f.resource}</span></p>
          )}
          <p className="text-xs text-gray-600 mb-1">{f.description}</p>
          {f.recommendation && (
            <p className="text-xs text-gray-700"><span className="font-medium">→ </span>{f.recommendation}</p>
          )}
        </div>
      </div>
    </div>
  );
}

const ComplianceModal: React.FC<Props> = ({
  isOpen, onClose, nodes, edges, provider, environment, architectureName, architectureId,
}) => {
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [report, setReport]               = useState<ComplianceReport | null>(null);
  const [metadata, setMetadata]           = useState<{ durationMs: number; tokensUsed: number } | null>(null);
  const [activeFramework, setActive]      = useState<FrameworkKey>('cis');
  const [fromCache, setFromCache]         = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState<string | undefined>();
  const [cacheContent, setCacheContent]   = useState<any>(null);
  const [selectedFrameworks]              = useState<ComplianceFramework[]>(['cis', 'iso27001', 'rgpd', 'hipaa', 'pci_dss']);

  useEffect(() => {
    if (!isOpen) return;
    setReport(null);
    setError(null);
    setActive('cis');
    setFromCache(false);
    setCacheUpdatedAt(undefined);
    setCacheContent(null);
    loadOrGenerate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadOrGenerate = async () => {
    if (architectureId) {
      try {
        const cached = await getAiCache(architectureId, 'compliance');
        setReport(cached.content.report);
        setMetadata(cached.content.metadata
          ? { durationMs: cached.content.metadata.durationMs, tokensUsed: cached.content.metadata.tokensUsed }
          : null);
        setFromCache(true);
        setCacheUpdatedAt(cached.updatedAt);
        setCacheContent(cached.content);
        return;
      } catch {
        // no cache
      }
    }
    generate();
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    setFromCache(false);
    setCacheUpdatedAt(undefined);
    try {
      const result = await scanCompliance({
        nodes, edges, provider, environment, architectureName, frameworks: selectedFrameworks,
      });
      setReport(result.report);
      setMetadata({ durationMs: result.metadata.durationMs, tokensUsed: result.metadata.tokensUsed });
      const payload = { report: result.report, metadata: result.metadata };
      setCacheContent(payload);
      if (architectureId) {
        saveAiCache(architectureId, 'compliance', payload).catch(() => {});
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors du scan de conformité');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (entry: AiCacheHistoryEntry) => {
    setReport(entry.content.report);
    setMetadata(entry.content.metadata
      ? { durationMs: entry.content.metadata.durationMs, tokensUsed: entry.content.metadata.tokensUsed }
      : null);
    setFromCache(true);
    setCacheUpdatedAt(entry.savedAt);
    setCacheContent(entry.content);
  };

  const handleExportPdf = () => {
    const w = window.open('', '_blank');
    if (!w || !report) return;
    const fwRows = FRAMEWORKS.map(f => {
      const fw = report.frameworks[f.key];
      if (!fw) return '';
      const statusCfg = STATUS_CONFIG[fw.status] || STATUS_CONFIG.not_applicable;
      return `<tr><td>${f.label}</td><td><span class="badge ${fw.status}">${statusCfg.label}</span></td><td>${fw.score}/100</td><td>${fw.findings.length} finding(s)</td></tr>`;
    }).join('');
    const findingsHtml = FRAMEWORKS.map(f => {
      const fw = report.frameworks[f.key];
      if (!fw || fw.findings.length === 0) return '';
      return `<h3>${f.label}</h3>${fw.findings.map(fi => `
        <div class="finding ${fi.severity}">
          <strong>[${fi.control}] ${fi.title}</strong> — <em>${fi.resource}</em><br/>
          <p>${fi.description}</p>
          <p><b>→</b> ${fi.recommendation}</p>
        </div>`).join('')}`;
    }).join('');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Compliance — ${architectureName || 'Architecture'}</title>
    <style>
      body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;color:#111}
      h1{font-size:20px;font-weight:700;margin-bottom:4px}h2{font-size:16px;font-weight:600;margin:20px 0 8px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
      h3{font-size:14px;font-weight:600;margin:16px 0 6px}
      .score{font-size:48px;font-weight:700;color:#6d28d9}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{background:#f3f4f6;text-align:left;padding:8px 12px;font-size:13px}
      td{padding:8px 12px;font-size:13px;border-bottom:1px solid #e5e7eb}
      .badge{padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600}
      .badge.compliant{background:#dcfce7;color:#166534}
      .badge.partial{background:#fef9c3;color:#854d0e}
      .badge.non_compliant{background:#fee2e2;color:#991b1b}
      .badge.not_applicable{background:#f3f4f6;color:#6b7280}
      .finding{border:1px solid #e5e7eb;border-radius:8px;padding:10px 14px;margin:6px 0;font-size:13px}
      .finding.critical{border-color:#fca5a5;background:#fef2f2}
      .finding.high{border-color:#fed7aa;background:#fff7ed}
      .finding.medium{border-color:#fde68a;background:#fffbeb}
      .finding.low{border-color:#bfdbfe;background:#eff6ff}
      ul{margin:6px 0;padding-left:20px}li{margin:3px 0;font-size:13px}
      @media print{body{padding:16px}}
    </style></head><body>`);
    w.document.write(`<h1>Rapport de Conformité — ${architectureName || ''}</h1>`);
    w.document.write(`<p>Score global : <span class="score">${report.overallScore}</span>/100</p>`);
    w.document.write(`<p style="font-size:14px;color:#374151">${report.summary}</p>`);
    w.document.write(`<h2>Vue d'ensemble par référentiel</h2><table><thead><tr><th>Référentiel</th><th>Statut</th><th>Score</th><th>Findings</th></tr></thead><tbody>${fwRows}</tbody></table>`);
    if (report.criticalActions?.length) {
      w.document.write(`<h2>Actions critiques</h2><ul>${report.criticalActions.map(a => `<li>${a}</li>`).join('')}</ul>`);
    }
    w.document.write(`<h2>Findings détaillés</h2>${findingsHtml}`);
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  if (!isOpen) return null;

  const activeData = report?.frameworks[activeFramework];
  const statusCfg = activeData ? (STATUS_CONFIG[activeData.status] || STATUS_CONFIG.not_applicable) : null;

  const totalFindings = report
    ? FRAMEWORKS.reduce((acc, f) => acc + (report.frameworks[f.key]?.findings?.length || 0), 0)
    : 0;
  const criticalCount = report
    ? FRAMEWORKS.reduce((acc, f) => acc + (report.frameworks[f.key]?.findings?.filter(fi => fi.severity === 'critical').length || 0), 0)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FiShield className="text-indigo-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">Scan de Conformité</h2>
            {report && (
              <span className="ml-2 text-xs text-gray-400 font-normal">
                {totalFindings} finding{totalFindings !== 1 ? 's' : ''}
                {criticalCount > 0 && <span className="ml-1 text-red-600 font-semibold">· {criticalCount} critique{criticalCount !== 1 ? 's' : ''}</span>}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={generate} disabled={loading}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-indigo-600 transition-colors px-2 py-1 rounded hover:bg-gray-50 disabled:opacity-40">
              <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Régénérer
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {loading && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <FiShield size={40} className="text-indigo-400 animate-pulse mb-4" />
              <p className="font-medium">Analyse de conformité en cours…</p>
              <p className="text-sm mt-1">Vérification CIS, ISO 27001, RGPD, HIPAA, PCI-DSS</p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              <FiAlertCircle className="inline mr-2" />{error}
            </div>
          )}

          {report && !loading && (
            <div className="space-y-6">

              {/* Overview */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 flex flex-col sm:flex-row gap-6">
                <div className="flex flex-col items-center justify-center gap-1">
                  <ScoreRing score={report.overallScore} />
                  <span className="text-xs text-gray-500 mt-1">Score global</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700 mb-3">{report.summary}</p>
                  <div className="space-y-1.5">
                    {FRAMEWORKS.map(f => {
                      const fw = report.frameworks[f.key];
                      if (!fw || fw.status === 'not_applicable') return null;
                      return <FrameworkBar key={f.key} score={fw.score} label={f.shortLabel} />;
                    })}
                  </div>
                </div>
                {report.certificationReadiness && (
                  <div className="border-l border-indigo-100 pl-4 min-w-[140px]">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Prêt à certifier</p>
                    {Object.entries(report.certificationReadiness).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-1.5 mb-1.5">
                        {val.ready
                          ? <FiCheckCircle size={13} className="text-green-500 shrink-0" />
                          : <FiAlertCircle size={13} className="text-red-400 shrink-0" />}
                        <span className="text-xs text-gray-600 uppercase font-mono">{key}</span>
                        {!val.ready && val.blockers > 0 && (
                          <span className="text-xs text-red-500 ml-auto">{val.blockers} bloquant{val.blockers !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Critical Actions */}
              {report.criticalActions?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                    <FiAlertCircle size={14} /> Actions critiques
                  </h3>
                  <ul className="space-y-1">
                    {report.criticalActions.map((action, i) => (
                      <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                        <span className="text-red-400 mt-0.5 shrink-0">→</span>{action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Framework tabs + findings */}
              <div>
                <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto">
                  {FRAMEWORKS.map(f => {
                    const fw = report.frameworks[f.key];
                    const active = activeFramework === f.key;
                    const statusColor = fw?.status === 'compliant'
                      ? 'text-green-600' : fw?.status === 'non_compliant'
                      ? 'text-red-600' : fw?.status === 'partial'
                      ? 'text-yellow-600' : 'text-gray-400';
                    return (
                      <button key={f.key}
                        onClick={() => setActive(f.key)}
                        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2.5 border-b-2 transition-colors whitespace-nowrap ${
                          active
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}>
                        <span className={active ? 'text-indigo-500' : statusColor}>{f.icon}</span>
                        {f.shortLabel}
                        {fw && fw.status !== 'not_applicable' && (
                          <span className={`ml-1 text-[10px] font-bold ${statusColor}`}>{fw.score}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {activeData && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusCfg?.badge}`}>
                        {statusCfg?.label}
                      </span>
                      <span className="text-xs text-gray-500">Score : <span className="font-semibold text-gray-700">{activeData.score}/100</span></span>
                    </div>

                    {activeData.status === 'not_applicable' && (
                      <p className="text-sm text-gray-500 text-center py-8">Ce référentiel n'est pas applicable à cette architecture.</p>
                    )}

                    {activeData.findings.length === 0 && activeData.status !== 'not_applicable' && (
                      <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                        <FiCheckCircle size={16} />
                        <span className="text-sm font-medium">Aucun problème de conformité détecté</span>
                      </div>
                    )}

                    <div className="space-y-2">
                      {activeData.findings.map((f, i) => (
                        <FindingCard key={i} f={f} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Compliance by resource */}
              {report.complianceByResource?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Conformité par ressource</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {report.complianceByResource.map((r, i) => {
                      const sev = r.worstSeverity as keyof typeof SEVERITY_CONFIG;
                      const cfg = SEVERITY_CONFIG[sev];
                      return (
                        <div key={i} className={`border rounded-lg p-2.5 flex items-center gap-2 ${r.issues === 0 ? 'border-green-200 bg-green-50' : (cfg?.border || 'border-gray-200')}`}>
                          {r.issues === 0
                            ? <FiCheckCircle size={14} className="text-green-500 shrink-0" />
                            : <span className="shrink-0">{cfg?.icon}</span>}
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{r.resourceLabel}</p>
                            <p className="text-[10px] text-gray-500">{r.resourceType} · {r.issues} problème{r.issues !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer */}
        {report && !loading && (
          <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between gap-4 bg-gray-50 rounded-b-2xl">
            <div className="text-xs text-gray-400 flex items-center gap-3">
              {metadata && (
                <>
                  <span>{metadata.tokensUsed.toLocaleString()} tokens</span>
                  <span>{(metadata.durationMs / 1000).toFixed(1)}s</span>
                </>
              )}
            </div>
            <AiCacheActions
              architectureId={architectureId}
              cacheType="compliance"
              content={cacheContent}
              fromCache={fromCache}
              updatedAt={cacheUpdatedAt}
              onRestore={handleRestore}
              onExportPdf={handleExportPdf}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceModal;
